import React, { useCallback, useEffect, useState } from 'react';
import {
  calculerDateExpirationProduit,
  calculerDureeProduction,
  getMentionExpiration,
} from '../utils/tracabilite';
import { api, uploadUrl } from '../hooks/useApi';
import ModalLierTransaction from './ModalLierTransaction';
import DateInput from './DateInput';
import ChampPostFin from './ChampPostFin';
import Icon from './Icon';

const LABEL_CAT = {
  viande:          'Viandes',
  produit_laitier: 'Produits laitiers',
  frais_legume:    'Légumes frais',
  frais_autre:     'Autres frais',
};

const ICON_CAT = {
  viande:          'viande',
  produit_laitier: 'laitier',
  frais_legume:    'legume',
  frais_autre:     'package',
};

function formatDate(d) {
  if (!d) return '—';
  return d.slice(0, 10);
}
function formatMontant(m) {
  if (m == null) return '—';
  return Math.abs(Number(m)).toFixed(2) + ' $';
}

/**
 * Section traçabilité & opérateurs.
 *
 * Props supplémentaires (étape 8) :
 *   batchId   : number — requis si batchStatut === 'finalise'
 *   batchStatut : string — 'brouillon' | 'en_cours' | 'finalise'
 *
 * Quand finalisé : les champs autorisés (operateurs, heures) utilisent ChampPostFin
 * pour déclencher PATCH direct. Les champs sous-condition (lot_fournisseur, dates)
 * ouvrent une modale de raison avant PATCH. Les champs du bloc Produit Fini restent
 * gérés dans SectionProduitFini.
 */
export function SectionTracabiliteCreation({ form, setForm, produit, batchId, batchStatut }) {
  const duree = calculerDureeProduction(form.heure_debut, form.heure_fin);
  const isFinalise = batchStatut === 'finalise';

  const syncForm = (champ, val) => setForm(prev => ({ ...prev, [champ]: val }));

  // Boutons opérateurs — post-finalisation : géré par ChampPostFin invisible + boutons visuels
  const [opSaving, setOpSaving] = useState(false);
  const [opToast, setOpToast] = useState('');

  const showOpToast = (msg, ok = true) => {
    setOpToast({ msg, ok });
    setTimeout(() => setOpToast(''), 2800);
  };

  const patchOperateur = async (val) => {
    if (!isFinalise) {
      syncForm('operateurs', val);
      return;
    }
    setOpSaving(true);
    try {
      await api.patch(`/batches/${batchId}/tracabilite-post-finalisation`, {
        champ: 'operateurs',
        nouvelle_valeur: val,
      });
      syncForm('operateurs', val);
      showOpToast('Opérateurs mis à jour ✓', true);
    } catch (e) {
      showOpToast(e?.response?.data?.error || 'Erreur lors de la mise à jour', false);
    } finally {
      setOpSaving(false);
    }
  };

  return (
    <div className="card form-card" style={{ marginTop: 16 }}>
      {opToast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: opToast.ok ? 'var(--success)' : 'var(--danger)',
          color: '#fff', padding: '10px 18px', borderRadius: 8,
          fontSize: 14, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          pointerEvents: 'none',
        }}>
          {opToast.msg}
        </div>
      )}

      <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 15, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="liste" size={16} /> Traçabilité &amp; opérateurs
      </h3>
      {isFinalise && (
        <p style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="idee" size={13} />
          Batch finalisé — les champs en or sont éditables et sauvegardés dans l'historique.
          Les champs <Icon name="verrou" size={12} /> sont verrouillés.
        </p>
      )}

      <div className="form-grid">
        <div className="form-field">
          <label>Lot fournisseur</label>
          <ChampPostFin
            batchId={batchId}
            champ="lot_fournisseur"
            valeurInitiale={form.lot_fournisseur || ''}
            type="text"
            statut={batchStatut || 'brouillon'}
            placeholder="ex: LOT-2026-0342"
            onChange={val => syncForm('lot_fournisseur', val)}
            onSuccess={val => syncForm('lot_fournisseur', val)}
            style={{ width: '100%' }}
          />
        </div>
        <div className="form-field">
          <label>Date d'achat matière</label>
          {isFinalise ? (
            <ChampPostFin
              batchId={batchId}
              champ="date_achat_matiere"
              valeurInitiale={form.date_achat_matiere || ''}
              type="date"
              statut={batchStatut || 'brouillon'}
              onChange={val => syncForm('date_achat_matiere', val)}
              onSuccess={val => syncForm('date_achat_matiere', val)}
              style={{ width: '100%' }}
            />
          ) : (
            <DateInput
              value={form.date_achat_matiere || ''}
              onChange={(v) => syncForm('date_achat_matiere', v)}
            />
          )}
        </div>
        <div className="form-field">
          <label>Date d'expiration MP</label>
          {isFinalise ? (
            <ChampPostFin
              batchId={batchId}
              champ="date_expiration_matiere"
              valeurInitiale={form.date_expiration_matiere || ''}
              type="date"
              statut={batchStatut || 'brouillon'}
              onChange={val => syncForm('date_expiration_matiere', val)}
              onSuccess={val => syncForm('date_expiration_matiere', val)}
              style={{ width: '100%' }}
            />
          ) : (
            <DateInput
              value={form.date_expiration_matiere || ''}
              onChange={(v) => syncForm('date_expiration_matiere', v)}
            />
          )}
        </div>
        <div className="form-field">
          <label>
            Opérateurs
            {isFinalise && (
              <span style={{ fontSize: 11, color: 'var(--accent)', marginLeft: 6 }}>
                (modifiable)
              </span>
            )}
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              ['LCU', 'LCU seul'],
              ['ELA', 'ELA seul'],
              ['LCU+ELA', 'LCU + ELA'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`btn ${form.operateurs === value ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => patchOperateur(value)}
                disabled={opSaving}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="form-field">
          <label>Heure début</label>
          <ChampPostFin
            batchId={batchId}
            champ="heure_debut"
            valeurInitiale={form.heure_debut || ''}
            type="time"
            statut={batchStatut || 'brouillon'}
            onChange={val => syncForm('heure_debut', val)}
            onSuccess={val => syncForm('heure_debut', val)}
            style={{ width: '100%' }}
          />
        </div>
        <div className="form-field">
          <label>Heure fin</label>
          <ChampPostFin
            batchId={batchId}
            champ="heure_fin"
            valeurInitiale={form.heure_fin || ''}
            type="time"
            statut={batchStatut || 'brouillon'}
            onChange={val => syncForm('heure_fin', val)}
            onSuccess={val => syncForm('heure_fin', val)}
            style={{ width: '100%' }}
          />
        </div>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
        {duree ? `Durée estimée : ${duree}` : `Durée estimée : saisir l'heure de début`}
      </div>
    </div>
  );
}

/**
 * Section produit fini.
 *
 * Props supplémentaires (étape 8) :
 *   batchId     : number — requis si batchStatut === 'finalise'
 *   batchStatut : string
 *
 * Post-finalisation : date_congelation / date_expiration_produit → ChampPostFin (sous_condition)
 *                     temp_conservation → ChampPostFin (autorise)
 *                     incident → champ en lecture seule (ajouter via FormCorrectionAppend dans EtapeTracabilite)
 */
export function SectionProduitFini({ produit, form, setForm, batchId, batchStatut }) {
  const dateCalculee = calculerDateExpirationProduit(produit, form.date_congelation);
  const mention = getMentionExpiration(produit);
  const isFinalise = batchStatut === 'finalise';

  const syncForm = (champ, val) => setForm(prev => ({ ...prev, [champ]: val }));

  return (
    <div className="card form-card" style={{ marginTop: 16 }}>
      <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 15, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="package" size={16} /> Produit fini
      </h3>
      {isFinalise && (
        <p style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="idee" size={13} />
          Batch finalisé — les dates (sous condition) et la température sont modifiables
          via l'historique. L'incident est en lecture seule — ajoutez une correction
          dans l'onglet Traçabilité.
        </p>
      )}

      <div className="form-grid">
        <div className="form-field">
          <label>Date de congélation</label>
          {isFinalise ? (
            <ChampPostFin
              batchId={batchId}
              champ="date_congelation"
              valeurInitiale={form.date_congelation || ''}
              type="date"
              statut={batchStatut || 'brouillon'}
              onChange={val => syncForm('date_congelation', val)}
              onSuccess={val => syncForm('date_congelation', val)}
              style={{ width: '100%' }}
            />
          ) : (
            <DateInput
              value={form.date_congelation || ''}
              onChange={(v) => {
                setForm((prev) => ({
                  ...prev,
                  date_congelation: v,
                  date_expiration_produit:
                    prev.date_expiration_produit === calculerDateExpirationProduit(produit, prev.date_congelation)
                      ? calculerDateExpirationProduit(produit, v)
                      : prev.date_expiration_produit,
                }));
              }}
            />
          )}
        </div>
        <div className="form-field">
          <label>Température conservation</label>
          {isFinalise ? (
            <ChampPostFin
              batchId={batchId}
              champ="temp_conservation"
              valeurInitiale={form.temp_conservation || '-18°C (congelé)'}
              type="select"
              options={[
                { value: '-18°C (congelé)', label: '-18°C (congelé)' },
                { value: '0-4°C (réfrigéré)', label: '0-4°C (réfrigéré)' },
              ]}
              statut={batchStatut || 'brouillon'}
              onChange={val => syncForm('temp_conservation', val)}
              onSuccess={val => syncForm('temp_conservation', val)}
              style={{ width: '100%' }}
            />
          ) : (
            <select
              value={form.temp_conservation || '-18°C (congelé)'}
              onChange={(e) => syncForm('temp_conservation', e.target.value)}
            >
              <option value="-18°C (congelé)">-18°C (congelé)</option>
              <option value="0-4°C (réfrigéré)">0-4°C (réfrigéré)</option>
            </select>
          )}
        </div>
        <div className="form-field">
          <label>Date d'expiration estimée</label>
          {isFinalise ? (
            <ChampPostFin
              batchId={batchId}
              champ="date_expiration_produit"
              valeurInitiale={form.date_expiration_produit || ''}
              type="date"
              statut={batchStatut || 'brouillon'}
              onChange={val => syncForm('date_expiration_produit', val)}
              onSuccess={val => syncForm('date_expiration_produit', val)}
              style={{ width: '100%' }}
            />
          ) : (
            <DateInput
              value={form.date_expiration_produit || ''}
              onChange={(v) => syncForm('date_expiration_produit', v)}
            />
          )}
          <span className="muted" style={{ fontSize: 12 }}>
            {mention}{dateCalculee ? ` · calculée : ${dateCalculee}` : ''}
          </span>
        </div>
      </div>
      <div className="form-field" style={{ marginTop: 12 }}>
        <label>
          Incident / déviation
          {isFinalise && (
            <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Icon name="verrou" size={11} /> lecture seule — ajouter une correction depuis l'onglet Traçabilité
            </span>
          )}
        </label>
        <textarea
          rows="3"
          value={form.incident || ''}
          onChange={(e) => !isFinalise && syncForm('incident', e.target.value)}
          readOnly={isFinalise}
          placeholder="ex: Panne fumoir 30 min, température compensée. Produit conforme."
          style={isFinalise ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
        />
      </div>
    </div>
  );
}

// ─── Section ingrédients frais — traçabilité factures ─────────────────────────

export function SectionIngredientsFrais({ batchId }) {
  const [ingredientsFrais, setIngredientsFrais] = useState([]);
  const [liaisons, setLiaisons]                 = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [modalIngredient, setModalIngredient]   = useState(null);

  const charger = useCallback(() => {
    setLoading(true);
    api.get(`/batches/${batchId}/ingredients-frais`)
      .then(data => {
        setIngredientsFrais(data.ingredients_frais || []);
        setLiaisons(data.liaisons_transactions || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [batchId]);

  useEffect(() => { charger(); }, [charger]);

  const retirerLiaison = async (liaisonId) => {
    try {
      await api.delete(`/batches/${batchId}/transactions-liees/${liaisonId}`);
      charger();
    } catch {}
  };

  if (loading) return null;
  if (ingredientsFrais.length === 0) return null;

  // Grouper par catégorie
  const categories = ['viande', 'produit_laitier', 'frais_legume', 'frais_autre'];
  const parCat = {};
  categories.forEach(cat => {
    parCat[cat] = ingredientsFrais.filter(i => i.categorie_tracabilite === cat);
  });

  // Index liaisons par matiere_id
  const liaisonsParMatiere = {};
  liaisons.forEach(l => {
    if (!liaisonsParMatiere[l.matiere_id]) liaisonsParMatiere[l.matiere_id] = [];
    liaisonsParMatiere[l.matiere_id].push(l);
  });

  return (
    <>
      <div className="card form-card" style={{ marginTop: 16 }}>
        <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 15, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-4)' }}>
          <Icon name="lien" size={16} /> Traçabilité des ingrédients frais
        </h3>
        <p className="muted" style={{ fontSize: 12, marginTop: -8, marginBottom: 16 }}>
          Détectés automatiquement depuis votre recette
        </p>

        {categories.map(cat => {
          const items = parCat[cat];
          if (!items || items.length === 0) return null;
          return (
            <div key={cat} style={{ marginBottom: 'var(--space-5)' }}>
              <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name={ICON_CAT[cat]} size={12} />
                {LABEL_CAT[cat]}
              </p>

              {items.map(ing => {
                const liaisonsIng = liaisonsParMatiere[ing.id] || [];
                return (
                  <div key={ing.id} style={{ marginBottom: 'var(--space-3)' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-2)', fontFamily: 'Cinzel, serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      {ing.nom}
                    </div>

                    {liaisonsIng.length === 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)', background: 'color-mix(in srgb, var(--warning) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--warning) 30%, transparent)', borderRadius: 'var(--radius-md)' }}>
                        <Icon name="attention" size={14} color="var(--warning)" />
                        <span className="muted" style={{ fontSize: 13 }}>Aucune transaction liée</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {liaisonsIng.map(l => (
                          <div key={l.id} style={{ padding: 'var(--space-3) var(--space-4)', background: 'color-mix(in srgb, var(--success) 7%, transparent)', border: '1px solid color-mix(in srgb, var(--success) 25%, transparent)', borderRadius: 'var(--radius-md)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <Icon name="check" size={13} weight="bold" color="var(--success)" />
                                  {l.fournisseur && <strong>{l.fournisseur}</strong>}
                                  {formatDate(l.date_trans)} — {formatMontant(l.montant)}
                                </div>
                                <div className="muted" style={{ fontSize: 12 }}>
                                  {l.description}
                                  {l.poids_utilise_kg && ` · ${l.poids_utilise_kg} ${ing.unite} utilisés dans ce batch`}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
                                {l.recu_fichier && (
                                  <a
                                    href={uploadUrl(`/uploads/recus/${l.recu_fichier}`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-ghost btn--sm"
                                  >
                                    <Icon name="document" size={13} /> Facture
                                  </a>
                                )}
                                <button
                                  className="btn btn-ghost btn--sm"
                                  style={{ color: 'var(--danger)' }}
                                  onClick={() => retirerLiaison(l.id)}
                                >
                                  <Icon name="fermer" size={13} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      className="btn btn-ghost btn--sm"
                      style={{ marginTop: 'var(--space-2)' }}
                      onClick={() => setModalIngredient(ing)}
                    >
                      <Icon name="lien" size={13} /> Lier une transaction
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {modalIngredient && (
        <ModalLierTransaction
          batchId={batchId}
          ingredient={modalIngredient}
          onClose={() => setModalIngredient(null)}
          onLiee={() => { setModalIngredient(null); charger(); }}
        />
      )}
    </>
  );
}
